<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
  useTemplateRef,
  watch
} from "vue";
import isString from "../../../utils/shared/isString";
import { useI18n } from "vue-i18n";
import { Size } from "../../../interfaces";
const modelValue = defineModel("modelValue", { type: null, ...{
  default: null
} });
const props = defineProps({
  isValid: {
    type: Boolean,
    default: true
  },
  size: {
    type: String,
    default: Size.MD
  },
  isTextarea: {
    type: Boolean,
    default: false
  },
  unit: {
    type: String,
    default: ""
  },
  errorMessage: {
    type: String,
    default: ""
  },
  disabled: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: ""
  },
  typeInput: {
    type: String,
    default: "text"
  },
  isSearch: {
    type: Boolean,
    default: false
  },
  isPassword: {
    type: Boolean,
    default: false
  },
  isFocus: {
    type: Boolean,
    default: false
  },
  noX: {
    type: Boolean,
    default: false
  },
  step: {
    type: Number,
    default: void 0
  },
  label: {
    type: String,
    default: void 0
  },
  tooltipText: {
    type: String,
    default: ""
  },
  id: {
    type: String,
    default: null,
    required: false
  },
  isOptional: {
    type: Boolean,
    default: false
  }
});
const emits = defineEmits([
  "input",
  "update:modelValue",
  "keyUpEnter",
  "blur",
  "change"
]);
const { t } = useI18n();
const uniqueId = useId();
const isRevealed = ref(false);
const chargemapInputElement = useTemplateRef("chargemapInput");
const hasValue = computed(() => {
  return !!modelValue.value;
});
const rightBlockElement = useTemplateRef("rightBlock");
const rightBlockWidth = ref(0);
const observerRightBlock = ref(null);
const leftBlockElement = useTemplateRef("leftBlock");
const leftBlockWidth = ref(0);
const observerLeftBlock = ref(null);
const canShowEyeIcon = computed(() => {
  return !!(props.isPassword && props.isValid && modelValue.value);
});
const canShowXIcon = computed(() => {
  return (!props.isPassword || !props.isValid) && !props.noX && !props.isTextarea;
});
const canShowTooltip = computed(() => {
  return !!props.tooltipText && !hasValue.value;
});
const getTypeInput = computed(() => {
  return props.isPassword && !isRevealed.value ? "password" : props.typeInput;
});
const handleReveal = () => {
  isRevealed.value = !isRevealed.value;
};
const updateValue = (value) => {
  let val = value;
  if (!isString(value)) {
    val = value.value;
  }
  modelValue.value = val;
  emits("update:modelValue", val);
};
const focusInput = () => {
  if (chargemapInputElement.value) {
    chargemapInputElement.value.focus();
  }
};
const updateRightBlockWidth = () => {
  if (rightBlockElement.value) {
    const updateWidth = () => {
      rightBlockWidth.value = rightBlockElement.value?.offsetWidth ?? 0;
    };
    observerRightBlock.value = new ResizeObserver(updateWidth);
    observerRightBlock.value.observe(rightBlockElement.value);
    updateWidth();
  }
};
onMounted(() => {
  if (props.isFocus) {
    focusInput();
  }
  updateRightBlockWidth();
  if (leftBlockElement.value) {
    const updateWidth = () => {
      leftBlockWidth.value = leftBlockElement.value?.offsetWidth ?? 0;
    };
    observerLeftBlock.value = new ResizeObserver(updateWidth);
    observerLeftBlock.value.observe(leftBlockElement.value);
    updateWidth();
  }
});
onBeforeUnmount(() => {
  if (observerRightBlock.value && rightBlockElement.value) {
    observerRightBlock.value.unobserve(rightBlockElement.value);
  }
  if (observerLeftBlock.value && leftBlockElement.value) {
    observerLeftBlock.value.unobserve(leftBlockElement.value);
  }
});
watch(
  () => props.isFocus,
  (val) => {
    if (val) {
      focusInput();
    }
  }
);
watch(
  () => modelValue.value,
  () => {
    updateRightBlockWidth();
  }
);
</script>

<template>
    <div class="flex flex-col w-full h-full gap-1.5 leading-6">
        <div class="flex justify-between text-sm" v-if="label || isOptional">
            <span v-if="label">{{ label }}</span>
            <span v-if="isOptional" class="font-regular text-gray-500 text-xs">
                {{ t("input.optional") }}
            </span>
        </div>

        <div class="relative flex items-center w-full h-full">
            <div
                v-if="isSearch || $slots['icon']"
                ref="leftBlock"
                class="absolute left-3 flex flex-inline items-center inset-y-0 gap-2 text-gray-500"
            >
                <IconSearch v-if="isSearch" class="size-5" />
                <slot name="icon" v-if="$slots['icon']" />
            </div>

            <component
                :is="isTextarea ? 'textarea' : 'input'"
                v-bind="$attrs"
                :id="id || uniqueId"
                ref="chargemapInput"
                class="w-full px-3 rounded-lg text-gray-800 tracking-tighter border border-slate-200 focus:ring-4 focus:ring-offset-0 focus-visible:outline-none shadow-sm transition ease-in-out duration-150"
                :class="[
  isTextarea && '!h-[120px]',
  !isValid ? 'focus:ring-rose-300/50 !border-rose-300' : 'placeholder:text-slate-500 focus:ring-primary-300/50 focus:border-primary-600',
  disabled && 'bg-slate-50 !placeholder:text-slate-400',
  size === Size.SM ? 'py-2 text-sm h-[37px]' : 'py-2.5 h-[44px]'
]"
                :style="{
  paddingRight: rightBlockWidth ? `${rightBlockWidth + 16}px` : '',
  paddingLeft: leftBlockWidth ? `${leftBlockWidth + 16}px` : ''
}"
                :value="modelValue"
                :placeholder
                :type="getTypeInput"
                :disabled
                :step
                @input="updateValue($event.target)"
                @keyup.enter="emits('keyUpEnter', $event)"
                @blur="emits('blur', $event)"
                @change="emits('change', $event)"
            />
            <div
                v-if="
  canShowEyeIcon || canShowXIcon && hasValue || canShowTooltip || unit
"
                ref="rightBlock"
                class="absolute right-3 inset-y-0 flex items-center gap-2"
            >
                <div
                    v-if="canShowEyeIcon"
                    class="right-block-icon"
                    @click.stop="handleReveal"
                >
                    <IconEyeSlash
                        v-if="isRevealed"
                        class="text-slate-500 size-4"
                    />
                    <IconEye v-else class="text-slate-500 size-4" />
                </div>

                <div v-if="canShowXIcon && hasValue" class="right-block-icon">
                    <IconX
                        class="size-4"
                        :class="isValid ? 'text-slate-500' : 'text-red-500'"
                        @click.prevent="updateValue('')"
                    />
                </div>

                <div
                    v-if="canShowTooltip"
                    class="right-block-icon"
                    @click.stop="handleReveal"
                >
                    <ChargemapTooltip :text="tooltipText">
                        <IconQuestionMarkCircleB2B
                            class="size-4 text-slate-500"
                        />
                    </ChargemapTooltip>
                </div>
                <div class="flex flex-inline items-center" v-if="unit">
                    {{ unit }}
                </div>
            </div>
        </div>
        <p v-show="!isValid && !!errorMessage" class="text-sm text-red-600">
            {{ errorMessage }}
        </p>
    </div>
</template>

<style scoped>
.right-block-icon {
  @apply flex items-center bg-gray-100 size-5 rounded-full cursor-pointer hover:bg-gray-200 hover:text-slate-800 justify-center;
}
</style>
